import React, { useState } from "react";
import { Mail, Phone, Linkedin, Github, Send, Download } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { useToast } from "../hooks/use-toast";
import { profile } from "../data/mock";

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({
        title: "Missing details",
        description: "Please fill in name, email, and message.",
        variant: "destructive",
      });
      return;
    }
    setSending(true);
    // Frontend-only: store locally for now
    const stored = JSON.parse(localStorage.getItem("portfolio_messages") || "[]");
    stored.push({ ...form, ts: new Date().toISOString() });
    localStorage.setItem("portfolio_messages", JSON.stringify(stored));
    setTimeout(() => {
      setSending(false);
      setForm({ name: "", email: "", message: "" });
      toast({
        title: "Message queued ✨",
        description: "Thanks! I'll get back to you within 48 hours.",
      });
    }, 700);
  };

  const handleDownload = () => {
    window.alert("Resume download will be wired to a real PDF.");
  };

  return (
    <section id="contact" className="relative py-24 lg:py-32 border-t border-teal-500/10">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-teal-500/40 to-transparent" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-teal-400 mb-4">
              <span className="w-8 h-px bg-teal-400" /> 08 — Contact
            </div>
            <h2 className="font-display text-4xl lg:text-5xl font-bold leading-tight">
              Let&apos;s build
              <br />
              <span className="text-teal-300">something good.</span>
            </h2>
            <p className="mt-5 text-slate-400 leading-relaxed">
              I&apos;m actively looking for full-time mechanical engineering roles for summer 2025. If you have a problem worth solving, I&apos;d love to hear about it.
            </p>

            <div className="mt-8 space-y-3">
              <a
                href={`mailto:${profile.email}`}
                className="flex items-center gap-3 p-4 rounded-xl border border-teal-500/15 bg-[#08201d]/60 hover:border-teal-400/40 hover:bg-[#0a2a26]/60 transition-colors group"
              >
                <Mail className="w-5 h-5 text-teal-300" />
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-wider text-slate-400">Email</div>
                  <div className="text-slate-100 group-hover:text-teal-200 truncate">{profile.email}</div>
                </div>
              </a>
              <a
                href={`tel:${profile.phone}`}
                className="flex items-center gap-3 p-4 rounded-xl border border-teal-500/15 bg-[#08201d]/60 hover:border-teal-400/40 hover:bg-[#0a2a26]/60 transition-colors group"
              >
                <Phone className="w-5 h-5 text-teal-300" />
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-400">Phone</div>
                  <div className="text-slate-100 group-hover:text-teal-200">{profile.phone}</div>
                </div>
              </a>
              <div className="flex gap-3">
                <a
                  href={profile.linkedin}
                  target="_blank"
                  rel="noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 p-4 rounded-xl border border-teal-500/15 bg-[#08201d]/60 hover:border-teal-400/40 text-slate-200 hover:text-teal-200 transition-colors"
                >
                  <Linkedin className="w-5 h-5" /> LinkedIn
                </a>
                <a
                  href={profile.github}
                  target="_blank"
                  rel="noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 p-4 rounded-xl border border-teal-500/15 bg-[#08201d]/60 hover:border-teal-400/40 text-slate-200 hover:text-teal-200 transition-colors"
                >
                  <Github className="w-5 h-5" /> GitHub
                </a>
              </div>
              <Button
                onClick={handleDownload}
                size="lg"
                className="w-full mt-2 bg-teal-400 text-[#04100f] hover:bg-teal-300 font-semibold h-12 shadow-[0_0_40px_-8px_rgba(45,212,191,0.5)]"
              >
                <Download className="w-5 h-5 mr-2" />
                Download Resume (PDF)
              </Button>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              className="rounded-2xl border border-teal-500/15 bg-[#08201d]/60 p-7 sm:p-9"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-slate-300">
                    Name
                  </Label>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Jane Doe"
                    className="bg-[#04100f] border-teal-500/20 focus-visible:ring-teal-400 text-slate-100 h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-300">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="jane@company.com"
                    className="bg-[#04100f] border-teal-500/20 focus-visible:ring-teal-400 text-slate-100 h-11"
                  />
                </div>
              </div>
              <div className="mt-5 space-y-2">
                <Label htmlFor="message" className="text-slate-300">
                  Message
                </Label>
                <Textarea
                  id="message"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Tell me about the role or project you have in mind..."
                  rows={6}
                  className="bg-[#04100f] border-teal-500/20 focus-visible:ring-teal-400 text-slate-100 resize-none"
                />
              </div>
              <Button
                type="submit"
                disabled={sending}
                size="lg"
                className="mt-6 bg-teal-400 text-[#04100f] hover:bg-teal-300 font-semibold h-12 px-6"
              >
                {sending ? "Sending..." : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send message
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
